import { nanoid } from 'nanoid';
import { subWeeks } from 'date-fns';
import { db } from '@/core/database/indexDB/database';
import { now } from '@/lib/utils';
import { OrderOption, Page, PageMetadata, PageSearchResult } from '../types/page.types';

export class PageRepository {
  async getNotesSince(syncTime: number | null) {
    const notes = await db.pages
      .where('updatedAt')
      .above(syncTime || 0)
      .toArray();

    return notes.map(n => ({
      ...n,
      createdAt: ((n.createdAt / 1000) | 0) * 1000,
      updatedAt: ((n.updatedAt / 1000) | 0) * 1000,
      archivedAt: n.archivedAt ? ((n.archivedAt / 1000) | 0) * 1000 : 0,
    }));
  }

  async getAllPages(orderBy: OrderOption = 'updatedAt'): Promise<Page[]> {
    if (orderBy !== 'orderNumber') {
      return db.pages.where('archivedAt').equals(0).reverse().sortBy(orderBy);
    }
    return db.pages.where('archivedAt').equals(0).sortBy(orderBy);
  }

  getFirstPage(orderBy: OrderOption = 'updatedAt') {
    return db.pages
      .where('archivedAt')
      .equals(0)
      .and(page => !page.collectionId)
      .reverse()
      .sortBy(orderBy)
      .then(pages => (pages.length ? pages[0] : undefined));
  }

  async getPagesMetadata(
    orderBy: OrderOption = 'updatedAt',
    filters: string[] = []
  ): Promise<PageMetadata[]> {
    let query = db.pagesMetadata
      .where('archivedAt')
      .equals(0)
      .and(page => !page.collectionId);
    if (filters.length) {
      query = query.and(page => {
        if (!page.tags) return false;
        const filterSet = new Set(filters);
        return page.tags.some(tag => filterSet.has(tag));
      });
    }
    const pages = await (orderBy === 'orderNumber'
      ? query.sortBy(orderBy)
      : query.reverse().sortBy(orderBy));

    return pages.map(getPageMetadataProps);
  }

  async getPageById(id: string): Promise<Page | undefined> {
    return db.pages.get(id);
  }

  async addPage(pageData?: Partial<Page>): Promise<string> {
    const pageId = pageData?.id || nanoid();
    const timestamp = now();
    const metadata: PageMetadata = {
      id: pageId,
      title: pageData?.title || 'Untitled',
      createdAt: timestamp,
      updatedAt: timestamp,
      archivedAt: 0,
      isPublic: pageData?.isPublic || false,
      tags: pageData?.tags || [],
      // for collection related page metadata
      collectionId: pageData?.collectionId || '',
      sectionId: pageData?.sectionId || '',
      orderNumber: pageData?.orderNumber || 0,
    };
    const newPage = { ...metadata, content: pageData?.content || '' };

    const exists = await db.pages.get(pageId);
    if (exists) return pageId;

    await db.transaction('rw', db.pages, db.pagesMetadata, async () => {
      await db.pages.add(newPage);
      await db.pagesMetadata.add(metadata);
    });
    return pageId;
  }

  async updatePage(id: string, patch: Partial<Page>) {
    const metadata = { ...patch };
    if ('content' in metadata) {
      delete metadata.content;
    }
    const updatedAt = now();
    await db.transaction('rw', db.pages, db.pagesMetadata, async () => {
      await db.pages.update(id, { ...patch, updatedAt });
      await db.pagesMetadata.update(id, { ...metadata, updatedAt });
    });
  }

  async updateIsPublic(id: string, isPublic: boolean) {
    return this.updatePage(id, { isPublic: isPublic });
  }

  async archivePage(id: string, archivedAt: number = Date.now()) {
    return this.updatePage(id, { archivedAt });
  }

  async unarchivePage(id: string) {
    await this.updatePage(id, { archivedAt: 0 });
    const page = await db.pages.get(id);
    return page;
  }

  async archivePages(ids: string[], archivedAt: number = Date.now()) {
    await this.updatePagesArchiveState(ids, archivedAt);
  }

  async unarchivePages(ids: string[]) {
    await this.updatePagesArchiveState(ids, 0);
  }

  async deletePage(id: string): Promise<void> {
    await db.transaction('rw', db.pages, db.pagesMetadata, async () => {
      await db.pages.delete(id);
      await db.pagesMetadata.delete(id);
    });
  }

  async searchPages(
    query: string,
    archived: boolean = false,
    collectionId?: string,
    includeContent: boolean = false,
    pageIds?: string[]
  ): Promise<PageSearchResult[]> {
    const lowercaseQuery = query.toLowerCase();
    const allowedIds = pageIds ? new Set(pageIds) : undefined;

    if (includeContent) {
      let pagesQuery = db.pages.reverse();
      pagesQuery = pagesQuery.and(page => (archived ? !!page.archivedAt : !page.archivedAt));
      if (collectionId) {
        pagesQuery = pagesQuery.and(page => page.collectionId === collectionId);
      }
      if (allowedIds) {
        pagesQuery = pagesQuery.and(page => allowedIds.has(page.id));
      }

      const pages = await pagesQuery.sortBy('updatedAt');
      return pages.reduce<PageSearchResult[]>((results, page) => {
        const titleMatches = page.title.toLowerCase().includes(lowercaseQuery);
        const contentText = getSearchableContent(page.content);
        const contentMatches = contentText.toLowerCase().includes(lowercaseQuery);

        if (!titleMatches && !contentMatches) return results;

        results.push({
          ...getPageMetadataProps(page),
          matchType: titleMatches ? 'title' : 'content',
          snippet: titleMatches ? undefined : getSearchSnippet(contentText, lowercaseQuery),
        });
        return results;
      }, []);
    }

    let pagesQuery = db.pagesMetadata.reverse();
    pagesQuery = pagesQuery.and(page => (archived ? !!page.archivedAt : !page.archivedAt));
    if (collectionId) {
      pagesQuery = pagesQuery.and(page => page.collectionId === collectionId);
    }
    if (allowedIds) {
      pagesQuery = pagesQuery.and(page => allowedIds.has(page.id));
    }
    pagesQuery = pagesQuery.and(page => page.title.toLowerCase().includes(lowercaseQuery));
    return await pagesQuery.sortBy('updatedAt');
  }

  async getArchivedPages(collectionId?: string): Promise<PageMetadata[]> {
    let query = db.pagesMetadata.where('archivedAt').notEqual(0);

    if (collectionId) {
      query = query.and(page => page.collectionId === collectionId);
    } else {
      query = query.and(page => !page.collectionId);
    }
    return query.reverse().sortBy('updatedAt');
  }

  private async updatePagesArchiveState(ids: string[], archivedAt: number): Promise<void> {
    if (!ids.length) return;

    const updatedAt = now();
    await db.transaction('rw', db.pages, db.pagesMetadata, async () => {
      await Promise.all([
        ...ids.map(id => db.pages.update(id, { archivedAt, updatedAt })),
        ...ids.map(id => db.pagesMetadata.update(id, { archivedAt, updatedAt })),
      ]);
    });
  }

  async cleanupOldArchivedPages(): Promise<void> {
    const oneWeekAgo = subWeeks(new Date(), 1).getTime();
    const pages = await db.pages
      .where('archivedAt')
      .notEqual(0)
      .and(page => !page.archivedAt || page.archivedAt < oneWeekAgo)
      .toArray();

    await this.bulkDeletePages(pages);
  }

  async bulkUpsertPages(pages: Page[]): Promise<void> {
    if (!pages.length) return;

    await db.transaction('rw', db.pages, db.pagesMetadata, async () => {
      await db.pages.bulkPut(pages);
      await db.pagesMetadata.bulkPut(pages.map(getPageMetadataProps));
    });
  }

  async bulkDeletePages(pages: Page[]): Promise<void> {
    if (!pages.length) return;

    const pageIds = pages.map(p => p.id);
    await db.transaction('rw', db.pages, db.pagesMetadata, async () => {
      await db.pages.bulkDelete(pageIds);
      await db.pagesMetadata.bulkDelete(pageIds);
    });
  }
}

export default new PageRepository();

export const getPageMetadataProps = ({
  id,
  title,
  orderNumber,
  createdAt,
  updatedAt,
  archivedAt,
  tags,
  isPublic,
  collectionId,
  sectionId,
}: PageMetadata) => ({
  id,
  title,
  orderNumber,
  createdAt,
  updatedAt,
  archivedAt,
  tags,
  isPublic,
  collectionId,
  sectionId,
});

function getSearchableContent(content: string) {
  if (!content) return '';

  try {
    const parsed = JSON.parse(content) as unknown;
    return collectText(parsed).join(' ');
  } catch {
    return content;
  }
}

function collectText(value: unknown): string[] {
  if (typeof value === 'string') return [value];
  if (!value || typeof value !== 'object') return [];
  if (Array.isArray(value)) return value.flatMap(collectText);

  return Object.values(value).flatMap(collectText);
}

function getSearchSnippet(content: string, lowercaseQuery: string) {
  const normalizedContent = content.replace(/\s+/g, ' ').trim();
  const matchIndex = normalizedContent.toLowerCase().indexOf(lowercaseQuery);
  if (matchIndex < 0) return normalizedContent.slice(0, 120);

  const start = Math.max(0, matchIndex - 45);
  const end = Math.min(normalizedContent.length, matchIndex + lowercaseQuery.length + 75);
  const prefix = start > 0 ? '...' : '';
  const suffix = end < normalizedContent.length ? '...' : '';

  return `${prefix}${normalizedContent.slice(start, end)}${suffix}`;
}
